import Cookies from 'js-cookie';
import {ONE_SECOND_MS, HALF_OF_SECOND_MS} from 'app/store/constants/storeConstants';

export type Manifest = Record<string, string>;
export interface PlayOpts { loop?: boolean; detune?: number }

export const cookiesSoundsVolume = (): number => Cookies.get('volume') || Number(window.settings.user.sound_enabled);

interface SoundInterface {
    key: keyof Manifest;
    opts: PlayOpts
}

const MAX_ATTEMPTS_FOR_RETRYING = 3;

export class MusicPlayer {
    private ctx: AudioContext;

    private gain: GainNode;

    private buffers: Map<string, AudioBuffer> = new Map();

    private manifest: Manifest;

    private pending: Array<SoundInterface> = [];

    constructor(manifest: Manifest, volume = 1) {
        this.manifest = manifest;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;

        if (!AudioCtx) {
            throw new Error('Web Audio API not supported in this browser');
        }

        this.ctx = new AudioCtx();
        this.gain = this.ctx.createGain();
        this.gain.gain.value = volume;
        this.gain.connect(this.ctx.destination);
    }

    async load(): Promise<void> {
        await Promise.all(Object.entries(this.manifest).map(async([key, url]) => {
            const res = await fetch(url);
            const data = await res.arrayBuffer();
            const buf = await this.ctx.decodeAudioData(data);
            this.buffers.set(key, buf);
        }));
    }

    // eslint-disable-next-line max-statements
    play(key: keyof Manifest, isMuted = false, opts: PlayOpts = {}): AudioBufferSourceNode | void {
        const cv = cookiesSoundsVolume();

        if (cv !== null && cv !== this.gain.gain.value) {
            this.setVolume(cv);
        }

        if (this.ctx.state !== 'running' || isMuted || cv === 0) {
            // In future can add to pending array (IOS) this.pending.push({ key, opts });
            return;
        }

        const buf = this.buffers.get(key as string);

        if (!buf) {
            console.warn(`MusicPlayer: buffer "${String(key)}" not loaded`);
            return;
        }

        const src = this.ctx.createBufferSource();
        src.buffer = buf;

        if (opts.loop) {
            src.loop = true;
        }

        if (typeof opts.detune === 'number') {
            src.detune.value = opts.detune;
        }

        src.connect(this.gain);
        src.start(0);
    }

    setVolume(v: number): void {
        this.gain.gain.value = Math.max(0, Math.min(1, v));
    }

    private async tryRun(count = 0, retry = MAX_ATTEMPTS_FOR_RETRYING): Promise<void> {
        if (count >= retry) {
            return;
        }

        try {
            setTimeout(async() => {
                if (this.ctx.state !== 'running') {
                    await this.tryRun(count + 1);
                }
            }, ONE_SECOND_MS + HALF_OF_SECOND_MS);
            await this.ctx.resume().then(() => console.log('✅ Sounds working'));
        } catch (_) {

            /* Ignore */
        }
    }

    unlock(): void {
        if (this.ctx.state === 'suspended') {
            this.tryRun();
        }

        if (this.ctx.state === 'running' && this.pending.length) {
            const q = [...this.pending];
            this.pending = [];
            q.forEach(({key, opts}) => this.play(key, false, opts));
        }
    }
}
